import type { MatrixPos, Priority } from "@app/core";
import { clamp01, matrixIndexOf, matrixPosForCell } from "@app/core";
import { Eye, EyeOff, cn } from "@app/ui";
import { useEffect, useRef, useState } from "react";
import type { I18nKey } from "../../lib/language.js";
import { AnimatePresence, motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { matrixCellWash, matrixColorHex, matrixScoreOf } from "../task-colors.js";
import { matrixDisplayWithUrgency } from "./useEffectiveMatrixDisplay.js";

interface Props {
  value: MatrixPos | null;
  /** Discrete priority — ghosts a dot for tasks with a flag but no stored position. */
  priority: Priority;
  /** Receives the new position; `urgencyAuto` on the pos marks the urgency axis
   *  as deadline-driven. A tap/drag always produces a MANUAL urgency. */
  onChange: (pos: MatrixPos | null) => void;
  className?: string;
  /** Urgency (0..1) auto-derived from the entry's deadline (null = no deadline /
   *  derivation off). Positions the dot/column when urgency is auto. */
  deadlineUrgency?: number | null;
  /** Whole-series "keep off the Priorities board/queue" flag. Omit `onHiddenChange`
   *  to leave the toggle out entirely (e.g. read-only embeds). */
  hidden?: boolean;
  onHiddenChange?: (hidden: boolean) => void;
}

const LONG_PRESS_MS = 400;
// Movement (px) that turns a tap into an exact-placement drag. Kept small so a
// SLIGHT reposition of the dot still commits its exact spot instead of falling
// through to the click handler (which would snap to a cell center or, on the
// active cell, clear the placement) — the "slight change didn't save" bug.
const DRAG_SLOP_PX = 6;

const LEVEL_KEYS: I18nKey[] = ["sidebar.priority.low", "sidebar.priority.medium", "sidebar.priority.high"];

interface Press {
  id: number;
  x: number;
  y: number;
  lastX: number;
  lastY: number;
  timer: number;
  initial: MatrixPos | null;
}

/**
 * 3×3 priority/urgency matrix picker, shared by the desktop paper form and the
 * mobile sheet. Tap a cell to snap to its center (tap the active cell again to
 * clear); press-and-hold or drag to place the dot at an exact position.
 */
export function PriorityMatrix({ value, priority, onChange, className, deadlineUrgency = null, hidden = false, onHiddenChange }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const pressRef = useRef<Press | null>(null);
  const fineRef = useRef(false);
  const suppressClickRef = useRef(false);
  const [fine, setFine] = useState(false);

  useEffect(() => () => {
    if (pressRef.current) window.clearTimeout(pressRef.current.timer);
  }, []);

  const display = matrixDisplayWithUrgency(value, priority, deadlineUrgency);
  const ghost = !value && !!display;
  const activeCol = display ? matrixIndexOf(display.urgency) : null;
  const activeRow = display ? matrixIndexOf(display.importance) : null;
  const dotColor = display ? matrixColorHex(matrixScoreOf(display)) : "transparent";
  // Urgency is auto (deadline-driven) for an unplaced task or a placed one with
  // the flag set. When auto AND a deadline implies a column, softly highlight
  // that column so the user sees where the system is placing urgency.
  const urgencyAuto = !value || !!value.urgencyAuto;
  const highlightCol = urgencyAuto && deadlineUrgency != null ? matrixIndexOf(deadlineUrgency) : null;
  // The auto toggle is only meaningful for a placed dot with a derivable deadline.
  const showAuto = !!value && deadlineUrgency != null;

  function setAuto(auto: boolean) {
    if (!value || !!value.urgencyAuto === auto) return;
    onChange(
      auto
        ? { urgency: value.urgency, importance: value.importance, urgencyAuto: true }
        : { urgency: deadlineUrgency ?? value.urgency, importance: value.importance },
    );
  }

  function posFromPoint(clientX: number, clientY: number): MatrixPos {
    const r = gridRef.current!.getBoundingClientRect();
    return {
      urgency: clamp01((clientX - r.left) / r.width),
      importance: clamp01(1 - (clientY - r.top) / r.height),
    };
  }

  function enterFine() {
    const p = pressRef.current;
    if (!p || fineRef.current) return;
    fineRef.current = true;
    setFine(true);
    try {
      gridRef.current?.setPointerCapture(p.id);
      (navigator as { vibrate?: (ms: number) => void }).vibrate?.(8);
    } catch { /* capture/haptics are best-effort */ }
    onChange(posFromPoint(p.lastX, p.lastY));
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Drop any STALE click-suppression from a gesture whose synthetic click
    // never arrived (drag released outside a cell, or a cancelled pointer).
    // A gesture that does synthesize a click delivers it right after its
    // pointerup — before this pointerdown — so an armed suppression that a
    // previous drag still needs cannot be dropped here.
    suppressClickRef.current = false;
    if (pressRef.current) window.clearTimeout(pressRef.current.timer);
    pressRef.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      timer: window.setTimeout(enterFine, LONG_PRESS_MS),
      initial: value ? { ...value } : null,
    };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const p = pressRef.current;
    if (!p || e.pointerId !== p.id) return;
    p.lastX = e.clientX;
    p.lastY = e.clientY;
    if (fineRef.current) {
      onChange(posFromPoint(e.clientX, e.clientY));
      return;
    }
    // A real drag skips the hold: grab the dot and place it directly.
    if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > DRAG_SLOP_PX) {
      window.clearTimeout(p.timer);
      enterFine();
    }
  }

  function onPointerLeave(e: React.PointerEvent<HTMLDivElement>) {
    // Pre-fine there is no pointer capture, so a press that slides off the
    // grid never delivers its pointerup here — the hold timer would later fire
    // enterFine on a possibly-released pointer and commit a stray placement.
    // Leaving the grid cancels the pending press instead. (In fine mode the
    // capture retargets all events to the grid; never cancel that drag.)
    const p = pressRef.current;
    if (!p || e.pointerId !== p.id || fineRef.current) return;
    window.clearTimeout(p.timer);
    pressRef.current = null;
  }

  function endPress(e: React.PointerEvent<HTMLDivElement>) {
    const p = pressRef.current;
    if (!p || e.pointerId !== p.id) return;
    window.clearTimeout(p.timer);
    pressRef.current = null;
    // A cancelled pointer (system gesture stole it, window lost focus) commits
    // NOTHING — and the browser synthesizes no click after it either.
    const cancelled = e.type === "pointercancel";
    const moved = Math.hypot(e.clientX - p.x, e.clientY - p.y) > DRAG_SLOP_PX;
    if (fineRef.current) {
      fineRef.current = false;
      setFine(false);
      if (cancelled) {
        // Fine mode writes live so the dot follows the pointer. Restore the
        // pre-gesture value when the OS/browser cancels capture; cancellation
        // must never become an accidental priority edit.
        onChange(p.initial);
        return;
      }
    } else if (moved && !cancelled) {
      // A slight drag that never crossed into fine mode still MOVED the dot —
      // commit the exact release position rather than letting the trailing
      // click snap it to a cell center (or clear it, on the active cell).
      onChange(posFromPoint(e.clientX, e.clientY));
    } else {
      return; // a true tap (onCellClick snaps/clears) or a cancelled gesture
    }
    // Swallow the click the browser synthesizes right after a hold/drag's
    // pointerup so it doesn't re-snap the just-placed dot. Consumed by the
    // next pointer click in onCellClick; keyboard activations bypass it; if no
    // click ever arrives (released outside a cell), the next pointerdown
    // drops the stale flag.
    suppressClickRef.current = true;
  }

  function onCellClick(e: React.MouseEvent, col: number, row: number) {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      // detail 0 ⇒ keyboard activation (Enter/Space) — never preceded by a
      // pointerdown, so a leftover suppression must not swallow it.
      if (e.detail > 0) return;
    }
    const isActive = !!value && activeCol === col && activeRow === row;
    onChange(isActive ? null : matrixPosForCell(col, row));
  }

  return (
    <div className={cn("flex w-fit select-none flex-col gap-1", className)}>
      <div className="flex items-stretch gap-1.5">
        {/* Importance axis: label centered on the axis, low/high at its ends. */}
        <div className="flex flex-col items-center justify-between py-1">
          <span className="text-[calc(9px*var(--app-text-scale))] lowercase text-muted-foreground/80">{t("matrix.high")}</span>
          <span className="rotate-180 text-[calc(10px*var(--app-text-scale))] font-medium uppercase tracking-wider text-muted-foreground [writing-mode:vertical-rl]">
            {t("matrix.importance")}
          </span>
          <span className="text-[calc(9px*var(--app-text-scale))] lowercase text-muted-foreground/80">{t("matrix.low")}</span>
        </div>
        <div
          ref={gridRef}
          role="radiogroup"
          aria-label={t("matrix.title")}
          className={cn(
            "relative grid aspect-square w-[11.5rem] grid-cols-3 grid-rows-3 gap-[3px] rounded-xl border bg-border/40 p-[3px]",
            fine ? "cursor-grabbing" : "cursor-pointer",
          )}
          style={{ touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPress}
          onPointerCancel={endPress}
          onPointerLeave={onPointerLeave}
          onContextMenu={(e) => e.preventDefault()}
        >
          {[2, 1, 0].map((row) =>
            [0, 1, 2].map((col) => {
              const isActive = activeCol === col && activeRow === row;
              const inHighlight = highlightCol === col && !isActive;
              const { color: cellColor, wash } = matrixCellWash(col, row);
              return (
                <button
                  key={`${col}-${row}`}
                  type="button"
                  role="radio"
                  data-matrix-cell={`${col}-${row}`}
                  aria-checked={!!value && isActive}
                  aria-label={t("matrix.cellLabel", { priority: t(LEVEL_KEYS[row]!), urgency: t(LEVEL_KEYS[col]!) })}
                  className="rounded-md bg-background transition-[box-shadow,filter] duration-150 hover:brightness-95 dark:hover:brightness-125"
                  style={{
                    backgroundImage: `linear-gradient(0deg, ${wash}, ${wash})`,
                    boxShadow: isActive
                      ? `inset 0 0 0 1.5px ${cellColor}${ghost ? "66" : ""}`
                      : inHighlight ? `inset 0 0 0 1.5px ${cellColor}55` : undefined,
                  }}
                  onClick={(e) => onCellClick(e, col, row)}
                />
              );
            }),
          )}
          <div className="pointer-events-none absolute inset-0">
            <AnimatePresence>
              {display && (
                <motion.div
                  key="dot"
                  className="absolute"
                  initial={{
                    // Mount AT the position (only scale/opacity pop in) — animating
                    // left/top from unset would spring the dot in from the corner.
                    left: `${display.urgency * 100}%`,
                    top: `${(1 - display.importance) * 100}%`,
                    scale: 0.4,
                    opacity: 0,
                  }}
                  animate={{
                    left: `${display.urgency * 100}%`,
                    top: `${(1 - display.importance) * 100}%`,
                    scale: fine ? 1.3 : 1,
                    opacity: ghost ? 0.45 : 1,
                  }}
                  exit={{ scale: 0.4, opacity: 0 }}
                  transition={
                    reduce
                      ? { duration: 0 }
                      : fine
                        ? { ...springs.snappy, left: { duration: 0 }, top: { duration: 0 } }
                        : springs.smooth
                  }
                  style={{ x: "-50%", y: "-50%" }}
                >
                  <span
                    className="block h-4 w-4 rounded-full border-2 border-background shadow-md transition-colors duration-150"
                    style={{ background: dotColor }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* Urgency axis under the grid: label centered, low/high at the ends.
          When the urgency mode is switchable, manual/auto buttons flank the
          label: low · manual · URGENCY · auto · high. */}
      <div className="ml-auto flex w-[11.5rem] items-center justify-between gap-1">
        <span className="text-[calc(9px*var(--app-text-scale))] lowercase text-muted-foreground/80">{t("matrix.low")}</span>
        {showAuto && (
          <button
            type="button"
            data-matrix-urgency-mode="manual"
            onClick={() => setAuto(false)}
            aria-pressed={!value!.urgencyAuto}
            title={t("matrix.manualUrgency.hint")}
            className={cn(
              "shrink-0 rounded-full border px-1.5 py-px text-[calc(9px*var(--app-text-scale))] font-medium lowercase tracking-wide transition-colors",
              !value!.urgencyAuto
                ? "border-primary bg-primary/15 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {t("matrix.manualUrgency")}
          </button>
        )}
        <span className="text-[calc(10px*var(--app-text-scale))] font-medium uppercase tracking-wider text-muted-foreground">
          {t("matrix.urgency")}
        </span>
        {showAuto && (
          <button
            type="button"
            data-matrix-urgency-mode="auto"
            onClick={() => setAuto(true)}
            aria-pressed={!!value!.urgencyAuto}
            title={t("matrix.autoUrgency.hint")}
            className={cn(
              "shrink-0 rounded-full border px-1.5 py-px text-[calc(9px*var(--app-text-scale))] font-medium lowercase tracking-wide transition-colors",
              value!.urgencyAuto
                ? "border-primary bg-primary/15 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {t("matrix.autoUrgency")}
          </button>
        )}
        <span className="text-[calc(9px*var(--app-text-scale))] lowercase text-muted-foreground/80">{t("matrix.high")}</span>
      </div>
      <div className="ml-auto flex w-[11.5rem] items-center justify-between gap-2">
        {onHiddenChange ? (
          <button
            type="button"
            data-matrix-hidden-toggle
            aria-pressed={hidden}
            title={t(hidden ? "matrix.hidden.show.hint" : "matrix.hidden.hide.hint")}
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-px text-[calc(9px*var(--app-text-scale))] font-medium lowercase tracking-wide transition-colors",
              hidden
                ? "border-primary bg-primary/15 text-primary"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
            onClick={() => onHiddenChange(!hidden)}
          >
            {hidden ? <EyeOff className="h-3 w-3" aria-hidden /> : <Eye className="h-3 w-3" aria-hidden />}
            {t(hidden ? "matrix.hidden.on" : "matrix.hidden.off")}
          </button>
        ) : <span />}
        <span className="min-w-0 truncate text-right text-[calc(10px*var(--app-text-scale))] text-muted-foreground/80">
          <AnimatePresence mode="wait" initial={false}>
            {value ? (
              <motion.button
                key="clear"
                type="button"
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={reduce ? { duration: 0 } : { duration: 0.15 }}
                onClick={() => onChange(null)}
              >
                {t("matrix.clear")}
              </motion.button>
            ) : (
              <motion.span
                key="hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={reduce ? { duration: 0 } : { duration: 0.15 }}
              >
                {t("matrix.hint")}
              </motion.span>
            )}
          </AnimatePresence>
        </span>
      </div>
    </div>
  );
}
